import {DataStatus} from 'platform/components';

import {useGetTenantQuery} from '@dms/api/core';
import {useGetDailyStockTakeByIdQuery} from '@dms/api/reporting';

import {type RequiredTestIdProps, suffixTestId} from 'shared/web';

import {DailyStockTakeDetailContent} from './DailyStockTakeDetailContent';

/**
 * Keeps the dialog from collapsing to a sliver while the row is still loading. Counted in design
 * system steps, not pixels: 60 steps resolve to 15rem (240px).
 */
const CONTENT_MIN_HEIGHT_STEPS = 60;

interface DailyStockTakeDetailDialogProps extends RequiredTestIdProps {
  /** Id of the clicked grid row, which is also the report row's own id. */
  id: string;
}

export function DailyStockTakeDetailDialog(props: DailyStockTakeDetailDialogProps) {
  const {data: detail, isLoading, isError} = useGetDailyStockTakeByIdQuery(props.id);
  /**
   * The amounts are in the tenant currency, so they are formatted with the tenant's own currency
   * rather than the viewer's display setting used by the dashboards in this lib.
   *
   * A failing tenant query is deliberately not an error for this dialog: currency is a formatting
   * detail, and the closing figures are worth showing without it. Its loading state is awaited so
   * the amounts do not flash as unavailable first.
   */
  const {data: tenant, isLoading: isTenantLoading} = useGetTenantQuery();

  return (
    <DataStatus
      data={detail}
      isLoading={isLoading || isTenantLoading}
      isError={isError}
      minHeight={CONTENT_MIN_HEIGHT_STEPS}
      data-testid={suffixTestId('status', props)}
    >
      {({data}) => (
        <DailyStockTakeDetailContent
          detail={data}
          currency={tenant?.currency}
          data-testid={props['data-testid']}
        />
      )}
    </DataStatus>
  );
}
